/**
 * BL-75 (D-210) · PlacementController — the DOM-agnostic brain.
 *
 * One state machine drives mouse, touch, and pen (TOUCH_PLACEMENT_SPEC §2/§9). It owns the
 * carry → aim → commit/cancel flow and writes the shared aim-state (`mouse`) the renderer
 * reads. It holds NO DOM, NO rendering, NO globals — every external touch is an injected
 * port/callback, so it is unit-testable with synthetic points.
 *
 * Placement RULE: commit depends on `mouse.canBuild` at UP — independent of drag distance
 * for MOUSE/PEN (0 px = a click/tap places; release on invalid/off-board cancels, nothing
 * placed). D-332 (owner decision, supersedes D-210's unified "0px commits" clause for
 * TOUCH only): on touch, a press that never exceeded the tap slop does NOT commit — a
 * continuous drag is required (accidental-build report). The expensive legality
 * (`armed.legal` ⇒ canPlaceAt's A*) is gated behind `valid && afford`.
 */
import type { MouseState } from "../game/inputHandlers.ts";
import type { Cell, PlaceableDescriptor, PreviewPort } from "./placementTypes.ts";
import {
  PLACEMENT_TUNING,
  type HapticKind,
  type PlacementTuning,
} from "./placementTuning.ts";

type PointerKind = "mouse" | "touch" | "pen";

export interface PlacementControllerDeps {
  /** Shared aim-state — the SAME object the renderer reads for the ghost/preview. */
  mouse: MouseState;
  /** Reroute-preview port (debounced once-per-cell). */
  preview: PreviewPort;
  /** The live armed placeable (derived from gs.selectedTowerType / placementMode), or null. */
  getArmed: () => PlaceableDescriptor | null;
  /** Convert a client point to a board cell (offset for touch/pen); null when no rect. */
  clientToCell: (clientX: number, clientY: number, pointerType: PointerKind) => Cell | null;
  /** Cache the board rect for this gesture (called at DOWN). */
  cacheRect: () => void;
  /** Is the grabbed element an armory chip (not the board)? */
  isChip: (target: unknown) => boolean;
  /** Arm the placeable a chip represents (sets the gs source of truth via the binding).
   *  Always arms — idempotent, no toggle (D-332 amendment: the toggle decision moved to
   *  UP, see `isChipArmed`/`disarmChip`). */
  armChip: (target: unknown) => void;
  /** Was this chip's type ALREADY the armed placeable, read BEFORE `armChip` runs at
   *  DOWN? Used at UP to decide a genuine tap-to-disarm vs. a fresh arm that fizzled
   *  into a non-committing press (which must stay armed). */
  isChipArmed: (target: unknown) => boolean;
  /** Disarm the current placeable — fired only for a motionless tap on a chip whose
   *  type was already armed before the press (F-5, corrected). */
  disarmChip: () => void;
  /** Existing select / firewall-sell / PT-17 deselect — returns true if it consumed the press. */
  tryHandleNonPlacement: (col: number, row: number) => boolean;
  /** CR-28 mouse-only hovered-tower range indicator. */
  setHoveredTower: (col: number, row: number) => void;
  clearHoveredTower: () => void;
  /** rAF (injectable so tests flush synchronously). */
  schedule?: (cb: () => void) => void;
  /** Haptics (progressive enhancement). */
  haptic?: (kind: HapticKind) => void;
  tuning?: PlacementTuning;
}

export class PlacementController {
  private readonly deps: PlacementControllerDeps;
  private readonly tuning: PlacementTuning;
  private readonly schedule: (cb: () => void) => void;

  private carrying = false;
  private grabSource: "chip" | "board" | null = null;
  /** Was the grabbed chip's type already armed BEFORE this press's `armChip` ran?
   *  Decides the tap-to-disarm toggle at UP (D-332 amendment — see `isChipArmed`). */
  private chipWasArmedAtDown = false;
  private lastCol = -1;
  private lastRow = -1;
  private startX = 0;
  private startY = 0;

  // PERF (audit): boundary-jitter cache — the cell + result "one recompute ago"
  // (i.e. the cell we just LEFT), so a bounce straight back to it inside
  // BOUNDARY_JITTER_MS can be answered from cache instead of re-running A*.
  private jitterCol = -1;
  private jitterRow = -1;
  private jitterAt = 0;
  private jitterValid = false;
  private jitterCanBuild = false;
  private jitterSealsPath = false;

  // rAF move-coalescing
  private rafPending = false;
  private pendingX: number | null = null;
  private pendingY = 0;
  private pendingKind: PointerKind = "mouse";

  constructor(deps: PlacementControllerDeps) {
    this.deps = deps;
    this.tuning = deps.tuning ?? PLACEMENT_TUNING;
    this.schedule =
      deps.schedule ??
      ((cb) =>
        typeof requestAnimationFrame === "function"
          ? requestAnimationFrame(cb)
          : cb());
  }

  isCarrying(): boolean {
    return this.carrying;
  }

  /** Reported state for tests/diagnostics — derived, never a stored duplicate of arming. */
  state(): "IDLE" | "ARMED" | "CARRYING" {
    if (this.carrying) return "CARRYING";
    return this.deps.getArmed() ? "ARMED" : "IDLE";
  }

  /** pointerdown. Returns true if a carry started (→ caller should `setPointerCapture`). */
  onDown(
    pt: { x: number; y: number },
    pointerType: PointerKind,
    hitTarget: unknown,
  ): boolean {
    if (this.carrying) return false; // a carry already owns the gesture (2nd finger → ignore)
    this.deps.cacheRect(); // cache the board rect for the WHOLE gesture (hit-test + aim)

    if (this.deps.isChip(hitTarget)) {
      // Grab from the rail: arm + carry so a drag onto the board carries the ghost.
      // D-332 amendment: read the PRE-arm state first — the toggle decision (disarm on
      // a genuine tap) is made at UP, once tap-vs-drag is known; `armChip` here always
      // arms (never toggles) so a fresh drag's ghost appears immediately at DOWN.
      this.chipWasArmedAtDown = this.deps.isChipArmed(hitTarget);
      this.deps.armChip(hitTarget);
      this.grabSource = "chip";
    } else {
      const armed = this.deps.getArmed();
      // Entity hit-test uses NO offset (the finger, not the aim-above point).
      const hit = this.deps.clientToCell(pt.x, pt.y, "mouse");
      if (!armed) {
        // Nothing armed → run the existing select/sell handling, never carry.
        if (hit) this.deps.tryHandleNonPlacement(hit.col, hit.row);
        return false;
      }
      if (!hit || !hit.inBounds) {
        if (hit) this.deps.tryHandleNonPlacement(hit.col, hit.row);
        return false; // off-board press while armed → no carry
      }
      // Press on an existing tower/firewall OR PT-17 edit-mode → handled, no carry.
      if (this.deps.tryHandleNonPlacement(hit.col, hit.row)) return false;
      this.grabSource = "board";
    }

    this.carrying = true;
    this.lastCol = -1;
    this.lastRow = -1;
    this.startX = pt.x;
    this.startY = pt.y;
    this.deps.haptic?.("grab");
    this.aim(pt.x, pt.y, pointerType);
    return true;
  }

  /** pointermove. Mouse/pen passive preview when not carrying; rAF-coalesced aim when carrying. */
  onMove(pt: { x: number; y: number }, pointerType: PointerKind): void {
    if (!this.carrying) {
      if (pointerType !== "touch") this.onHover(pt, pointerType);
      return;
    }
    this.pendingX = pt.x;
    this.pendingY = pt.y;
    this.pendingKind = pointerType;
    if (!this.rafPending) {
      this.rafPending = true;
      this.schedule(() => this.flush());
    }
  }

  /** pointerup. Commit iff `mouse.canBuild` (any drag distance on mouse/pen); on TOUCH
   *  ONLY, a press that never exceeded the tap slop is blocked from committing (D-332 —
   *  supersedes D-210's "0px commits" clause for touch specifically). Else cancel. */
  onUp(pt: { x: number; y: number }, pointerType: PointerKind): void {
    if (!this.carrying) return;
    this.aim(pt.x, pt.y, pointerType);
    // F-15: `applyAim`'s per-cell perf gate can skip recomputation when the released
    // cell is unchanged from the last aimed cell, so a grid mutation that happened
    // while the finger simply RESTED (a breach, a spawn-detection flip) could leave a
    // stale `canBuild` that then commits an illegal placement. Force a fresh read of
    // ONLY the gating fields right here — no preview/haptic side effects, so this can
    // never change what mid-drag rendering SHOWS, only what UP is allowed to COMMIT.
    this.revalidateAtUp(pt.x, pt.y, pointerType);
    const armed = this.deps.getArmed();
    const moved =
      Math.hypot(pt.x - this.startX, pt.y - this.startY) >
      this.tuning.TAP_SLOP_PX;
    const wasCommittable = this.deps.mouse.canBuild && !!armed;

    // D-332 (owner decision, touch-only): a touch press that never exceeded the tap
    // slop must NOT commit — a zero-distance tap on a touchscreen is an accidental
    // brush, not an intentional placement (owner report: accidental builds on
    // device). Keyed on `"touch"` specifically — mouse AND pen still commit at any
    // drag distance (incl. 0), byte-identical to the pre-D-332 model. This applies
    // uniformly to BOTH grab sources (chip-drag and board-press); in practice it can
    // only ever fire for a board-press — a chip's aim point sits off the board, so
    // `wasCommittable` is never true for a motionless chip tap (that case is the
    // existing chip-toggle branch below, unaffected).
    const touchTapBlocked = pointerType === "touch" && !moved && wasCommittable;

    if (touchTapBlocked) {
      // A rejected touch tap must NOT read as an illegal-cell rejection — its own cue.
      this.deps.haptic?.("tapRejected");
    } else if (wasCommittable) {
      armed.commit(this.deps.mouse.col, this.deps.mouse.row);
      this.deps.haptic?.("commit");
    } else if (this.grabSource === "chip" && !moved) {
      // A pure chip tap (no movement) — the toggle decision (D-332 amendment): only
      // disarm if this chip's type was ALREADY armed BEFORE `onDown` ran `armChip`
      // (a genuine tap-to-cancel on the currently-armed chip). A press that armed a
      // NEW/different type and then fizzled (no drag, no commit) must stay armed — the
      // arm from onDown already stands, so there's nothing to do in that case.
      if (this.chipWasArmedAtDown) {
        this.deps.disarmChip();
      }
    } else {
      this.deps.haptic?.("invalid"); // released on invalid/off-board → cancel, nothing placed
    }
    this.endGesture();
  }

  /** pointercancel / Escape (mid-drag) / right-click → cancel the carry. */
  onCancel(): void {
    if (this.carrying) this.endGesture();
  }

  /** pointerleave (mouse/pen, not carrying) → clear the passive preview + hovered tower. */
  onHoverEnd(pointerType: PointerKind): void {
    if (pointerType === "touch") return;
    if (this.carrying) return;
    this.deps.preview.clear();
    this.deps.clearHoveredTower();
    this.deps.mouse.col = -1;
    this.deps.mouse.row = -1;
    this.deps.mouse.valid = false;
    this.deps.mouse.canBuild = false;
    this.deps.mouse.sealsPath = false;
    this.lastCol = -1;
    this.lastRow = -1;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private flush(): void {
    this.rafPending = false;
    // The gesture may have ended (commit/cancel) before this frame ran — drop the stale
    // point so a late rAF never resurrects the ghost after release.
    if (!this.carrying) {
      this.pendingX = null;
      return;
    }
    if (this.pendingX != null) {
      this.aim(this.pendingX, this.pendingY, this.pendingKind);
      this.pendingX = null;
    }
  }

  /** Mouse/pen passive hover: update the hovered-tower indicator (CR-28) + aim. */
  private onHover(pt: { x: number; y: number }, pointerType: PointerKind): void {
    const cell = this.deps.clientToCell(pt.x, pt.y, pointerType);
    if (!cell) return; // no rect yet
    this.deps.setHoveredTower(cell.col, cell.row); // find-at-cell (OOB → null inside dep)
    this.applyAim(cell);
  }

  private aim(x: number, y: number, pointerType: PointerKind): void {
    const cell = this.deps.clientToCell(x, y, pointerType);
    if (!cell) return;
    this.applyAim(cell);
  }

  private now(): number {
    return typeof performance !== "undefined" ? performance.now() : Date.now();
  }

  /** The §9 aim body: snap → gate validity/affordability → gated A* → preview. */
  private applyAim(cell: Cell): void {
    const { mouse, preview } = this.deps;
    if (!cell.inBounds) {
      mouse.col = cell.col;
      mouse.row = cell.row;
      mouse.valid = false;
      mouse.canBuild = false;
      mouse.sealsPath = false;
      preview.clear();
      this.lastCol = -1;
      this.lastRow = -1;
      this.jitterCol = -1;
      this.jitterRow = -1;
      return;
    }
    if (cell.col === this.lastCol && cell.row === this.lastRow) return; // perf: no cell change

    // PERF (audit): a finger/cursor resting exactly on a cell BOUNDARY can flip back
    // and forth between the SAME two cells many times a second — each flip re-ran the
    // full cost (canPlaceAt's A* + previewAllPixelPathsWithBlock's one-A*-per-path). A
    // bounce straight back to the cell we just LEFT, inside BOUNDARY_JITTER_MS, reuses
    // that cell's already-computed result instead. A genuine forward drag across NEW
    // cells never revisits `jitterCol/jitterRow`, so it always recomputes fresh — this
    // only dampens the boundary-flicker case. `onUp` never reaches this (it forces a
    // fresh read via `revalidateAtUp`), so the actual valid&&afford gate is untouched.
    const now = this.now();
    if (
      cell.col === this.jitterCol &&
      cell.row === this.jitterRow &&
      now - this.jitterAt < this.tuning.BOUNDARY_JITTER_MS
    ) {
      // The verdict for the cell we're ENTERING (== the current jitter
      // KEY) — capture it before the re-pair below overwrites the cache.
      const enterValid = this.jitterValid;
      const enterCanBuild = this.jitterCanBuild;
      const enterSealsPath = this.jitterSealsPath;
      // Re-pair the cache: `mouse.*` currently describe the cell we're
      // LEAVING (lastCol/lastRow) — cache them under that cell's key
      // (mirrors the fresh-compute path below) instead of just swapping
      // the key and leaving the stale values attached to it.
      this.jitterCol = this.lastCol;
      this.jitterRow = this.lastRow;
      this.jitterValid = mouse.valid;
      this.jitterCanBuild = mouse.canBuild;
      this.jitterSealsPath = mouse.sealsPath ?? false;
      this.lastCol = cell.col;
      this.lastRow = cell.row;
      mouse.col = cell.col;
      mouse.row = cell.row;
      mouse.valid = enterValid;
      mouse.canBuild = enterCanBuild;
      mouse.sealsPath = enterSealsPath;
      if (mouse.canBuild) preview.update(cell.col, cell.row);
      else preview.clear();
      return;
    }

    const prevCanBuild = mouse.canBuild;
    this.jitterCol = this.lastCol;
    this.jitterRow = this.lastRow;
    this.jitterValid = mouse.valid;
    this.jitterCanBuild = mouse.canBuild;
    this.jitterSealsPath = mouse.sealsPath ?? false;
    this.jitterAt = now;
    this.lastCol = cell.col;
    this.lastRow = cell.row;
    mouse.col = cell.col;
    mouse.row = cell.row;

    const armed = this.deps.getArmed();
    if (!armed) {
      mouse.valid = false;
      mouse.canBuild = false;
      mouse.sealsPath = false;
      preview.clear();
      return;
    }

    const valid = armed.eligible(cell.col, cell.row);
    mouse.valid = valid;
    // PERF: legality (A*) gated behind valid && affordable — never run A* for an
    // ineligible OR unaffordable cell (the real mousemove short-circuit).
    let canBuild = false;
    let sealsPath = false;
    if (valid && armed.canAfford()) {
      canBuild = armed.legal(cell.col, cell.row);
      // Eligible + affordable but illegal ⇒ the only reason canPlaceAt failed is route
      // reachability (eligibility already passed) ⇒ this cell would SEAL the route.
      sealsPath = !canBuild;
    }
    mouse.canBuild = canBuild;
    mouse.sealsPath = sealsPath;

    if (canBuild) {
      preview.update(cell.col, cell.row);
      if (!prevCanBuild) this.deps.haptic?.("validEnter");
    } else {
      preview.clear();
    }
  }

  /** F-15 — force a fresh eligibility/affordability/legality read at UP, independent
   *  of `applyAim`'s cell-unchanged short-circuit (and never dampened by the boundary-
   *  jitter cache above). No preview/haptic writes — those stay owned by `applyAim` /
   *  `endGesture`; this only guards what `onUp` is allowed to COMMIT. */
  private revalidateAtUp(x: number, y: number, pointerType: PointerKind): void {
    const cell = this.deps.clientToCell(x, y, pointerType);
    if (!cell || !cell.inBounds) return; // aim() above already reflects an OOB release
    const { mouse } = this.deps;
    const armed = this.deps.getArmed();
    if (!armed) return;
    const valid = armed.eligible(cell.col, cell.row);
    mouse.valid = valid;
    let canBuild = false;
    let sealsPath = false;
    if (valid && armed.canAfford()) {
      canBuild = armed.legal(cell.col, cell.row);
      sealsPath = !canBuild;
    }
    mouse.canBuild = canBuild;
    mouse.sealsPath = sealsPath;
  }

  private endGesture(): void {
    this.carrying = false;
    this.grabSource = null;
    this.chipWasArmedAtDown = false;
    this.pendingX = null; // drop any queued move so a late flush is a no-op (cross-gesture safe)
    this.deps.preview.clear();
    this.lastCol = -1;
    this.lastRow = -1;
    this.jitterCol = -1;
    this.jitterRow = -1;
    // Hide the ghost until the next hover/press (sticky-arm keeps gs arming untouched).
    this.deps.mouse.valid = false;
    this.deps.mouse.canBuild = false;
    this.deps.mouse.sealsPath = false;
  }
}
